import { AavoDataGridMobileViewConfig, FlattenedAavoGridColDef } from "src/components/common/dataGrid/types.tsx";
import { GridRenderCellParams, GridRowId, GridValidRowModel } from "@mui/x-data-grid-pro";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { BottomNavigation, BottomNavigationAction, Typography } from "@mui/material";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { mergeSx } from "src/utils/styles.ts";
import { positionAbsoluteFullSize } from "src/styles/sx.ts";
import { useStoredState } from "src/utils/useStoredState.ts";
import i18n from "i18next";
import { FormattedColumnValue } from "src/components/common/dataGrid/mobileView/FormattedColumnValue.tsx";

export interface AavoDataGridMobileViewDetailsViewProps<RowData extends GridValidRowModel>
	extends RowSelfDataDetailsTabContentProps<RowData> {
	row: RowData;
	detailsViewConfigProvider: AavoDataGridMobileViewConfig<RowData>["detailsView"] | undefined;
	refreshData?: () => Promise<unknown>;
	gridId: string;
}

export const AavoDataGridMobileViewDetailsView = <RowData extends GridValidRowModel>({
	row,
	detailsViewConfigProvider,
	gridId,
	rowId,
	api,
	columns,
	refreshData,
}: AavoDataGridMobileViewDetailsViewProps<RowData>) => {
	const [selectedTabIndex, setSelectedTabIndex] = useStoredState({
		defaultValue: 0,
		type: "number",
		key: `${gridId}_mobileDetailsViewSelectedTabIndex`,
	});

	const config = detailsViewConfigProvider?.({ row: row, refreshData: refreshData });

	const notNullTabs = config?.items.filter((item): item is Exclude<typeof item, false> => item !== false) ?? [];
	const selfTab = {
		title: i18n.t("information"),
		icon: faInfoCircle,
		content: <RowSelfDataDetailsTabContent rowId={rowId} columns={columns} api={api} />,
	};
	const tabs = [...(config?.showDefaultDetailsTab === false ? [] : [selfTab]), ...notNullTabs];

	if (tabs.length === 0) {
		return null;
	}
	const fixedSelectedTabIndex = Math.max(Math.min(selectedTabIndex, tabs.length - 1), 0);

	return (
		<VerticalBox
			sx={{
				flex: 1,
			}}
		>
			<VerticalBox
				sx={{
					flex: 1,
					position: "relative",
				}}
			>
				{tabs.map((tab, index) => {
					const isActive = index === fixedSelectedTabIndex;
					return (
						<VerticalBox
							key={index}
							sx={mergeSx(positionAbsoluteFullSize, {
								flex: 1,
								alignItems: "stretch",
								opacity: isActive ? 1 : 0,
								pointerEvents: isActive ? undefined : "none",
							})}
						>
							{tab.content}
						</VerticalBox>
					);
				})}
			</VerticalBox>
			{tabs.length > 1 && (
				<BottomNavigation
					showLabels
					value={fixedSelectedTabIndex}
					onChange={(_, newValue) => setSelectedTabIndex(newValue)}
					sx={{
						overflowX: "auto",
						display: "flex",
						flexDirection: "row",
						justifyContent: "space-evenly",
					}}
				>
					{tabs.map((item, index) => (
						<BottomNavigationAction
							key={index}
							sx={{
								padding: 0,
								minWidth: 0,
								flexGrow: 1,
								flexShrink: 1,
								flexBasis: 0,
								alignItems: "center",
								"& .MuiBottomNavigationAction-label": {
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
									width: "100%",
									textAlign: "center",
								},
							}}
							label={item.title}
							icon={item.icon && <FontAwesomeIcon icon={item.icon} />}
						/>
					))}
				</BottomNavigation>
			)}
		</VerticalBox>
	);
};

interface RowSelfDataDetailsTabContentProps<RowData extends GridValidRowModel> {
	api: GridRenderCellParams["api"];
	columns: FlattenedAavoGridColDef<RowData>[];
	rowId: GridRowId;
}

const RowSelfDataDetailsTabContent = <RowData extends GridValidRowModel>({
	rowId,
	columns,
	api,
}: RowSelfDataDetailsTabContentProps<RowData>) => {
	// The dialog may outlive the grid row (e.g. row removed by a data refresh).
	if (api.getRow(rowId) == null) return null;

	const detailsColumns = columns.filter((column) => column.mobile !== "none");

	return (
		<VerticalBox
			sx={{
				overflow: "auto",
			}}
		>
			<VerticalBox
				sx={{
					flex: "auto 0 0",
					margin: 1,
					gap: 1,
				}}
			>
				{detailsColumns.map((column) => (
					<HorizontalBox
						key={column.field}
						sx={{
							gap: 0.5,
							alignItems: "center",
							flexWrap: "wrap",
						}}
					>
						<Typography sx={{ fontWeight: "bold" }}>{column.headerName}:</Typography>
						<FormattedColumnValue
							api={api}
							rowId={rowId}
							column={column}
							typographySx={{
								whiteSpace: "pre-wrap",
							}}
						/>
					</HorizontalBox>
				))}
			</VerticalBox>
		</VerticalBox>
	);
};
